import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SITES } from "@/lib/sites";
import { LOCATION_TYPES, LOCATION_HINT } from "@/lib/locations";

const CATEGORIES = {
  IT: ["Hardware", "Software", "Network", "Account/Access", "Email", "Printer", "Other"],
  Maintenance: ["HVAC", "Plumbing", "Electrical", "Lighting", "Furniture", "Building/Grounds", "Other"],
  Custodial: ["Cleaning", "Restroom", "Trash/Recycling", "Spill", "Restock Supplies", "Floor Care", "Other"],
  Mechanic:  ["Vehicle Repair", "Fleet Service", "Small Engine", "Tire/Brake", "Fuel/Fluids", "Preventive Maintenance", "Other"],
};

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function NewTicketSheet({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const isSubmitterOnBehalf = user?.role === "secretary" || user?.role === "principal";
  const userSites = user?.sites || (user?.site ? [user.site] : []);
  const isSiteMaster = user?.role === "admin" || userSites.includes("District") || user?.role === "it";
  const allowedSites = isSiteMaster ? SITES : SITES.filter((s) => userSites.includes(s.code));
  const availableSites = allowedSites.length > 0 ? allowedSites : SITES;
  const canPickSite = availableSites.length > 1 || isSiteMaster;
  const defaultSite = userSites[0] || "District";

  const [form, setForm] = useState({
    title: "",
    description: "",
    department: "IT",
    category: "Hardware",
    priority: "Medium",
    site: defaultSite,
    location_type: "Classroom",
    location: "",
    requester_name: "",
    requester_email: "",
  });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [onBehalf, setOnBehalf] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const MAX_FILES = 5;
  const MAX_PER_FILE = 5 * 1024 * 1024;   // 5MB
  const MAX_TOTAL = 25 * 1024 * 1024;     // 25MB per ticket
  const ALLOWED_TYPES = new Set([
    "image/png", "image/jpeg", "image/webp", "image/gif", "image/heic", "image/heif",
    "application/pdf",
    "text/plain", "text/csv",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/zip", "application/x-zip-compressed",
  ]);

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    const room = MAX_FILES - files.length;
    if (room <= 0) { toast.error(`Max ${MAX_FILES} files per ticket`); e.target.value = ""; return; }
    let runningTotal = files.reduce((s, f) => s + (f.size || 0), 0);
    const parsed = [];
    for (const f of list.slice(0, room)) {
      if (f.type && !ALLOWED_TYPES.has(f.type)) { toast.error(`${f.name}: type not allowed`); continue; }
      if (f.size > MAX_PER_FILE) { toast.error(`${f.name} exceeds 5MB`); continue; }
      if (runningTotal + f.size > MAX_TOTAL) { toast.error(`Total attachment size exceeds 25MB`); break; }
      const data = await fileToBase64(f);
      parsed.push({ name: f.name, type: f.type, size: f.size, data });
      runningTotal += f.size;
    }
    if (list.length > room) toast.info(`Only added first ${room} — max ${MAX_FILES} files per ticket`);
    setFiles((prev) => [...prev, ...parsed]);
    e.target.value = "";
  };

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description required"); return;
    }
    if (!form.location.trim()) {
      toast.error("Location is required — tell us where the issue is"); return;
    }
    if (!form.site) {
      toast.error("Site is required"); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, attachments: files };
      if (!onBehalf) {
        // Not submitting on behalf — don't send empty strings that break EmailStr validation
        delete payload.requester_name;
        delete payload.requester_email;
      } else {
        if (!payload.requester_name?.trim()) delete payload.requester_name;
        if (!payload.requester_email?.trim()) delete payload.requester_email;
      }
      await api.post("/tickets", payload);
      toast.success("Ticket submitted");
      setForm({ title: "", description: "", department: "IT", category: "Hardware", priority: "Medium", site: defaultSite, location_type: "Classroom", location: "", requester_name: "", requester_email: "" });
      setFiles([]);
      setOnBehalf(false);
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="new-ticket-sheet">
        <SheetHeader>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">New request</div>
          <SheetTitle className="font-heading text-2xl text-zinc-950">Submit a ticket</SheetTitle>
          <SheetDescription className="text-zinc-500">
            Provide enough detail so the team can start work fast.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          {isSubmitterOnBehalf && (
            <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={onBehalf}
                  onChange={(e) => setOnBehalf(e.target.checked)}
                  data-testid="on-behalf-toggle"
                  className="mt-0.5 accent-[#0055FF]"
                />
                <span>
                  <span className="inline-flex items-center gap-1 font-medium text-zinc-800">
                    <UserPlus className="h-3.5 w-3.5" /> Submit on behalf of a teacher
                  </span>
                  <span className="block text-xs text-zinc-500 mt-0.5">
                    The ticket's requester name & email will show as the teacher's for follow-up.
                  </span>
                </span>
              </label>
              {onBehalf && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                  <Input
                    placeholder="Teacher name"
                    value={form.requester_name}
                    onChange={(e) => set("requester_name", e.target.value)}
                    className="h-9 rounded-md"
                    data-testid="onbehalf-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="teacher@esusd.org"
                    value={form.requester_email}
                    onChange={(e) => set("requester_email", e.target.value)}
                    className="h-9 rounded-md"
                    data-testid="onbehalf-email-input"
                  />
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Short summary"
              data-testid="ticket-title-input"
              className="h-11 rounded-md focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Site</Label>
            {canPickSite ? (
              <Select value={form.site} onValueChange={(v) => set("site", v)}>
                <SelectTrigger data-testid="ticket-site-trigger" className="h-11 rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableSites.map((s) => (<SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            ) : (
              <div className="h-11 rounded-md border border-zinc-200 bg-zinc-50 px-3 flex items-center text-sm text-zinc-700" data-testid="ticket-site-locked">
                {SITES.find((s) => s.code === form.site)?.label || form.site}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Department</Label>
              <Select value={form.department} onValueChange={(v) => { set("department", v); set("category", CATEGORIES[v][0]); }}>
                <SelectTrigger data-testid="ticket-department-trigger" className="h-11 rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="IT">IT</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                  <SelectItem value="Custodial">Custodial</SelectItem>
                  <SelectItem value="Mechanic">Mechanic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="ticket-category-trigger" className="h-11 rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES[form.department].map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="ticket-priority-trigger" className="h-11 rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Low","Medium","High","Critical"].map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Location type <span className="text-red-500">*</span></Label>
              <Select value={form.location_type} onValueChange={(v) => set("location_type", v)}>
                <SelectTrigger data-testid="ticket-location-type-trigger" className="h-11 rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">
              Location details <span className="text-red-500">*</span>
            </Label>
            <Input
              value={form.location}
              onChange={(e) => set("location", e.target.value)}
              placeholder={LOCATION_HINT[form.location_type] || "e.g. Room 12"}
              data-testid="ticket-location-input"
              required
              className={`h-11 rounded-md ${!form.location.trim() ? "border-zinc-300" : "border-zinc-300"} focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500`}
            />
            <p className="text-[11px] text-zinc-500">Required — this is how the team finds the issue on-site.</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="What's happening? Steps to reproduce, impact, etc."
              rows={5}
              data-testid="ticket-description-input"
              className="rounded-md focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Attachments</Label>
            <label className="flex items-center gap-2 border border-dashed border-zinc-300 rounded-md px-4 py-3 cursor-pointer hover:bg-zinc-50 transition-colors duration-200">
              <Paperclip className="h-4 w-4 text-zinc-500" />
              <span className="text-sm text-zinc-600">Attach files (max 5 files · 5MB each · 25MB total)</span>
              <input type="file" multiple className="hidden" onChange={onFiles} data-testid="ticket-file-input" />
            </label>
            {files.length > 0 && (
              <ul className="space-y-1 text-xs text-zinc-600">
                {files.map((f, i) => (
                  <li key={`${f.name}-${f.size}-${f.lastModified ?? i}`} className="flex items-center justify-between bg-zinc-50 rounded px-3 py-1.5">
                    <span className="truncate">{f.name}</span>
                    <button className="text-zinc-400 hover:text-red-500" onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <SheetFooter className="mt-8 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ticket-cancel-button">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="ticket-submit-button"
            className="bg-[#0055FF] hover:bg-[#0040CC] text-white transition-colors duration-200"
          >
            {busy ? "Submitting…" : "Submit ticket"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
